import { useQuery } from "@tanstack/react-query";
import { buildSendTransaction, simulateSolanaFee } from "@/lib/solana";
import { getObliqFeePayer } from "@/lib/obliq";
import { GASLESS_DISPLAY_SOL } from "@/lib/fees";
import { lamportsToSol } from "@/lib/utils";

interface UseSendFeeEstimateOptions {
	fromAddress: string | undefined;
	toAddress: string | undefined;
	tokenAddress: string | undefined;
	amount: number;
	decimals: number;
	enabled?: boolean;
}

export interface FeeEstimate {
	feeSol: number;
	isGasless: boolean;
}

async function estimateFee(
	fromAddress: string,
	toAddress: string,
	tokenAddress: string,
	amount: number,
	decimals: number,
): Promise<FeeEstimate> {
	const feePayer = await getObliqFeePayer();
	const tx = await buildSendTransaction(fromAddress, toAddress, tokenAddress, amount, decimals, feePayer);
	const result = await simulateSolanaFee(tx);
	return {
		feeSol: lamportsToSol(result.feeLamports),
		isGasless: true,
	};
}

export function useSendFeeEstimate({
	fromAddress,
	toAddress,
	tokenAddress,
	amount,
	decimals,
	enabled = true,
}: UseSendFeeEstimateOptions) {
	const canEstimate =
		enabled &&
		Boolean(fromAddress) &&
		Boolean(toAddress) &&
		Boolean(tokenAddress) &&
		amount > 0;

	return useQuery<FeeEstimate>({
		queryKey: ["sendFee", fromAddress, toAddress, tokenAddress, amount, decimals],
		queryFn: () =>
			estimateFee(fromAddress!, toAddress!, tokenAddress!, amount, decimals),
		enabled: canEstimate,
		staleTime: 30_000,
		gcTime: 60_000,
		retry: 1,
		placeholderData: { feeSol: GASLESS_DISPLAY_SOL, isGasless: true },
	});
}
